/**
 * Low-level state mutations plus trigger collection.
 *
 * Everything that changes GameState goes through here so that every change
 * emits exactly one GameEvent and so that triggers are collected in one place.
 * Callers pass a Ctx which carries the state being built, the event list and
 * the RNG cursor.
 */

import type { Chain, Keyword, TriggerEvent } from './cardTypes';
import { getTokenDef, profileOf } from './cardDb';
import { Rng } from './rng';
import {
  getEffectiveStats,
  labelOf,
  matchesFilter,
  nameOf,
  opponentOf,
  profile,
  rowFor,
  walletExtraProduction,
  evalCondition,
} from './query';
import type { GameEvent, GameObject, GameState, StackItem, Zone } from './types';

export interface Ctx {
  state: GameState;
  events: GameEvent[];
  rng: Rng;
}

export function makeCtx(state: GameState): Ctx {
  return { state, events: [], rng: new Rng(state.rngState) };
}

/** Write the RNG cursor back into the state. Call once per reduce. */
export function syncRng(ctx: Ctx): void {
  ctx.state.rngState = ctx.rng.state;
}

export function emit(ctx: Ctx, event: GameEvent): void {
  ctx.events.push(event);
  ctx.state.eventLog.push(event);
}

export function nextId(ctx: Ctx, prefix: string): string {
  const n = ctx.state.nextId++;
  return `${prefix}${n}`;
}

/** The marker counter used to hand a Drainer Link kill to the state-based checks. */
export const DRAINER_MARK = 'drainerLinkMarked';

// ---------------------------------------------------------------------------
// Zone movement
// ---------------------------------------------------------------------------

function removeFromCurrentZone(state: GameState, o: GameObject): void {
  const p = state.players[o.controller];
  const owner = state.players[o.owner];
  switch (o.zone) {
    case 'deck':
      pull(owner.deck, o.instanceId);
      break;
    case 'hand':
      pull(owner.hand, o.instanceId);
      break;
    case 'dust':
      pull(owner.dust, o.instanceId);
      break;
    case 'blacklist':
      pull(owner.blacklist, o.instanceId);
      break;
    case 'battlefield': {
      pull(p.battlefield.characters, o.instanceId);
      pull(p.battlefield.wallets, o.instanceId);
      pull(p.battlefield.protocols, o.instanceId);
      pull(p.battlefield.upgrades, o.instanceId);
      break;
    }
    case 'stack':
      state.stack = state.stack.filter((s) => s.sourceInstanceId !== o.instanceId || s.kind !== 'card');
      break;
    default:
      break;
  }
}

function pull(list: string[], id: string): void {
  const i = list.indexOf(id);
  if (i >= 0) list.splice(i, 1);
}

export interface MoveOptions {
  /** Deck insertion position. Default 'top'. */
  deckPosition?: 'top' | 'bottom';
  /** Reset combat, damage and buff state. Default true for anything leaving the battlefield. */
  resetState?: boolean;
  /** Turn the object entered the battlefield, for `enteredTurn`. */
  enteringTurn?: number;
}

/** Move an object between zones. Does not fire triggers; callers do that. */
export function moveObject(ctx: Ctx, instanceId: string, zone: Zone, options: MoveOptions = {}): void {
  const state = ctx.state;
  const o = state.objects[instanceId];
  if (!o) return;
  const leavingBattlefield = o.zone === 'battlefield' && zone !== 'battlefield';
  removeFromCurrentZone(state, o);
  o.zone = zone;

  if (leavingBattlefield || options.resetState) {
    o.damage = 0;
    o.buffs = [];
    o.grantedKeywords = [];
    o.removedKeywords = [];
    o.attacking = undefined;
    o.blocking = undefined;
    o.frozen = false;
    o.exhausted = false;
    o.attachedTo = undefined;
    o.counters = {};
    o.oncePerTurnUsed = [];
    o.xValue = undefined;
  }

  const owner = state.players[o.owner];
  switch (zone) {
    case 'deck':
      if (options.deckPosition === 'bottom') owner.deck.push(instanceId);
      else owner.deck.unshift(instanceId);
      o.enteredTurn = -1;
      break;
    case 'hand':
      owner.hand.push(instanceId);
      o.enteredTurn = -1;
      break;
    case 'dust':
      owner.dust.push(instanceId);
      o.enteredTurn = -1;
      break;
    case 'blacklist':
      owner.blacklist.push(instanceId);
      o.enteredTurn = -1;
      break;
    case 'battlefield': {
      const row = rowFor(profile(state, instanceId).type);
      const p = state.players[o.controller];
      if (row) p.battlefield[row].push(instanceId);
      o.enteredTurn = options.enteringTurn ?? state.turn;
      o.controlSinceTurn = options.enteringTurn ?? state.turn;
      break;
    }
    default:
      o.enteredTurn = -1;
      break;
  }
}

// ---------------------------------------------------------------------------
// Card flow
// ---------------------------------------------------------------------------

/** Draw n cards. Missing cards cost 2 Integrity each (RULES 3). */
export function drawCards(ctx: Ctx, playerId: string, n: number): string[] {
  const state = ctx.state;
  const p = state.players[playerId];
  const drawn: string[] = [];
  let missing = 0;
  for (let i = 0; i < n; i++) {
    const top = p.deck[0];
    if (!top) {
      missing++;
      continue;
    }
    moveObject(ctx, top, 'hand');
    drawn.push(top);
    emit(ctx, {
      type: 'CardDrawn',
      turn: state.turn,
      player: playerId,
      instanceId: top,
      cardName: nameOf(state, top),
    });
  }
  if (missing > 0) {
    p.deckExhaustionCount += missing;
    emit(ctx, { type: 'DeckExhausted', turn: state.turn, player: playerId, amount: missing });
    changeIntegrity(ctx, playerId, -2 * missing, 'deck exhaustion');
  }
  return drawn;
}

export function millCards(ctx: Ctx, playerId: string, n: number): string[] {
  const p = ctx.state.players[playerId];
  const milled: string[] = [];
  for (let i = 0; i < n; i++) {
    const top = p.deck[0];
    if (!top) break;
    moveObject(ctx, top, 'dust');
    milled.push(top);
  }
  if (milled.length > 0) {
    emit(ctx, { type: 'Milled', turn: ctx.state.turn, player: playerId, count: milled.length });
  }
  return milled;
}

export function discardCard(ctx: Ctx, playerId: string, instanceId: string): void {
  const o = ctx.state.objects[instanceId];
  if (!o || o.zone !== 'hand') return;
  const name = nameOf(ctx.state, instanceId);
  moveObject(ctx, instanceId, 'dust');
  emit(ctx, { type: 'Discarded', turn: ctx.state.turn, player: playerId, instanceId, name });
}

export function shuffleDeck(ctx: Ctx, playerId: string): void {
  ctx.rng.shuffle(ctx.state.players[playerId].deck);
}

// ---------------------------------------------------------------------------
// Integrity and damage
// ---------------------------------------------------------------------------

export function changeIntegrity(ctx: Ctx, playerId: string, delta: number, reason: string): void {
  if (delta === 0) return;
  const p = ctx.state.players[playerId];
  p.integrity += delta;
  emit(ctx, {
    type: 'IntegrityChanged',
    turn: ctx.state.turn,
    player: playerId,
    delta,
    total: p.integrity,
    reason,
  });
  if (delta > 0) fireTriggers(ctx, 'integrity:gain', { controller: playerId });
}

/**
 * Deal damage to a character. Applies Vamp for the source and marks Drainer
 * Link kills for the state-based checks. Returns the damage actually dealt.
 */
export function dealDamage(
  ctx: Ctx,
  sourceInstanceId: string | null,
  targetInstanceId: string,
  amount: number,
): number {
  const state = ctx.state;
  if (amount <= 0) return 0;
  const target = state.objects[targetInstanceId];
  if (!target || target.zone !== 'battlefield') return 0;
  target.damage += amount;
  const sourceName = sourceInstanceId ? nameOf(state, sourceInstanceId) : 'An effect';
  emit(ctx, {
    type: 'DamageDealt',
    turn: state.turn,
    player: sourceInstanceId ? state.objects[sourceInstanceId]?.controller ?? target.controller : target.controller,
    sourceInstanceId,
    sourceName,
    targetInstanceId,
    targetName: nameOf(state, targetInstanceId),
    amount,
  });
  if (sourceInstanceId) {
    const stats = getEffectiveStats(state, sourceInstanceId);
    if (stats.keywords.includes('drainerLink')) {
      target.counters[DRAINER_MARK] = 1;
    }
    applyVamp(ctx, sourceInstanceId, amount);
  }
  return amount;
}

export function damagePlayer(
  ctx: Ctx,
  sourceInstanceId: string | null,
  targetPlayer: string,
  amount: number,
): number {
  if (amount <= 0) return 0;
  const state = ctx.state;
  const sourceName = sourceInstanceId ? nameOf(state, sourceInstanceId) : 'An effect';
  emit(ctx, {
    type: 'DamageDealtToPlayer',
    turn: state.turn,
    player: sourceInstanceId ? state.objects[sourceInstanceId]?.controller ?? targetPlayer : targetPlayer,
    sourceInstanceId,
    sourceName,
    targetPlayer,
    amount,
  });
  changeIntegrity(ctx, targetPlayer, -amount, 'damage');
  if (sourceInstanceId) {
    applyVamp(ctx, sourceInstanceId, amount);
    const src = state.objects[sourceInstanceId];
    if (src && src.zone === 'battlefield' && profileOf(src).type === 'character') {
      fireTriggers(ctx, 'self:damagePlayer', { sourceInstanceId });
    }
  }
  return amount;
}

function applyVamp(ctx: Ctx, sourceInstanceId: string, amount: number): void {
  const src = ctx.state.objects[sourceInstanceId];
  if (!src) return;
  const stats = getEffectiveStats(ctx.state, sourceInstanceId);
  if (!stats.keywords.includes('vamp')) return;
  emit(ctx, {
    type: 'VampRestored',
    turn: ctx.state.turn,
    player: src.controller,
    sourceName: nameOf(ctx.state, sourceInstanceId),
    amount,
  });
  changeIntegrity(ctx, src.controller, amount, 'vamp');
}

// ---------------------------------------------------------------------------
// Leaving the battlefield
// ---------------------------------------------------------------------------

/** Destroy: HODL prevents it. Returns true when the object actually died. */
export function destroyObject(ctx: Ctx, instanceId: string, reason: string): boolean {
  const state = ctx.state;
  const o = state.objects[instanceId];
  if (!o || o.zone !== 'battlefield') return false;
  const stats = getEffectiveStats(state, instanceId);
  if (stats.keywords.includes('hodl')) return false;
  emit(ctx, {
    type: 'Destroyed',
    turn: state.turn,
    player: o.controller,
    instanceId,
    name: nameOf(state, instanceId),
    reason,
  });
  return dieObject(ctx, instanceId);
}

/**
 * Move a permanent from the battlefield to the Dust as a death. Fires
 * `self:death` and `character:death`. Tokens then cease to exist.
 */
export function dieObject(ctx: Ctx, instanceId: string): boolean {
  const state = ctx.state;
  const o = state.objects[instanceId];
  if (!o || o.zone !== 'battlefield') return false;
  const name = nameOf(state, instanceId);
  const isCharacter = profileOf(o).type === 'character';
  const controller = o.controller;

  // Triggers read the dying object's own abilities and the board as it was,
  // so collect them before the move.
  fireTriggers(ctx, 'self:death', { sourceInstanceId: instanceId });
  if (isCharacter) fireTriggers(ctx, 'character:death', { triggeringInstanceId: instanceId });

  moveObject(ctx, instanceId, 'dust');
  emit(ctx, { type: 'Died', turn: state.turn, player: controller, instanceId, name });
  ceaseIfToken(ctx, instanceId);
  return true;
}

export function sacrificeObject(ctx: Ctx, instanceId: string): boolean {
  const state = ctx.state;
  const o = state.objects[instanceId];
  if (!o || o.zone !== 'battlefield') return false;
  emit(ctx, {
    type: 'Sacrificed',
    turn: state.turn,
    player: o.controller,
    instanceId,
    name: nameOf(state, instanceId),
  });
  return dieObject(ctx, instanceId);
}

/** Bounce to the owner's hand. No death trigger. Tokens cease to exist. */
export function bounceObject(ctx: Ctx, instanceId: string): void {
  const state = ctx.state;
  const o = state.objects[instanceId];
  if (!o || o.zone !== 'battlefield') return;
  const name = nameOf(state, instanceId);
  const controller = o.controller;
  o.controller = o.owner;
  moveObject(ctx, instanceId, 'hand');
  emit(ctx, { type: 'Bounced', turn: state.turn, player: controller, instanceId, name });
  ceaseIfToken(ctx, instanceId);
}

export function blacklistObject(ctx: Ctx, instanceId: string): void {
  const state = ctx.state;
  const o = state.objects[instanceId];
  if (!o) return;
  const name = nameOf(state, instanceId);
  const controller = o.controller;
  moveObject(ctx, instanceId, 'blacklist');
  emit(ctx, { type: 'Blacklisted', turn: state.turn, player: controller, instanceId, name });
  ceaseIfToken(ctx, instanceId);
}

/** Tokens outside the battlefield cease to exist (RULES 8.6). */
export function ceaseIfToken(ctx: Ctx, instanceId: string): void {
  const state = ctx.state;
  const o = state.objects[instanceId];
  if (!o || !o.tokenId) return;
  if (o.zone === 'battlefield' || o.zone === 'limbo') return;
  const name = nameOf(state, instanceId);
  const controller = o.controller;
  moveObject(ctx, instanceId, 'limbo');
  emit(ctx, { type: 'TokenCeased', turn: state.turn, player: controller, instanceId, name });
}

/** The legendary rule (RULES 8.5). Not a death and not a destruction. */
export function legendaryRuleRemove(ctx: Ctx, instanceId: string): void {
  const state = ctx.state;
  const o = state.objects[instanceId];
  if (!o) return;
  const name = nameOf(state, instanceId);
  const controller = o.controller;
  moveObject(ctx, instanceId, 'dust');
  emit(ctx, { type: 'LegendaryRuleApplied', turn: state.turn, player: controller, instanceId, name });
  ceaseIfToken(ctx, instanceId);
}

// ---------------------------------------------------------------------------
// Entering the battlefield
// ---------------------------------------------------------------------------

/** Put a permanent onto the battlefield and fire deploy triggers. */
export function enterBattlefield(
  ctx: Ctx,
  instanceId: string,
  options: { exhausted?: boolean; attachedTo?: string } = {},
): void {
  const state = ctx.state;
  const o = state.objects[instanceId];
  if (!o) return;
  moveObject(ctx, instanceId, 'battlefield');
  const prof = profileOf(o);
  if (prof.type === 'upgrade' && options.attachedTo) {
    o.attachedTo = options.attachedTo;
    emit(ctx, {
      type: 'UpgradeAttached',
      turn: state.turn,
      player: o.controller,
      instanceId,
      name: prof.name,
      hostName: nameOf(state, options.attachedTo),
    });
  }
  if (options.exhausted) o.exhausted = true;
  emit(ctx, {
    type: 'Deployed',
    turn: state.turn,
    player: o.controller,
    instanceId,
    name: prof.name,
    cardType: prof.type,
  });
  fireTriggers(ctx, 'self:deploy', { sourceInstanceId: instanceId });
  if (prof.type === 'character') {
    fireTriggers(ctx, 'character:deploy', { triggeringInstanceId: instanceId });
  }
  if (prof.type === 'wallet') {
    fireTriggers(ctx, 'wallet:install', { controller: o.controller });
  }
}

export function createTokenObject(ctx: Ctx, playerId: string, tokenId: string): string | null {
  const state = ctx.state;
  // Fail closed on an unknown token id rather than create a blank body.
  const definition = getTokenDef(tokenId);
  if (!definition) return null;
  const def = profileOf({ tokenId });
  const instanceId = nextId(ctx, 'tk');
  const o: GameObject = {
    instanceId,
    tokenId,
    controller: playerId,
    owner: playerId,
    zone: 'limbo',
    exhausted: false,
    frozen: false,
    damage: 0,
    buffs: [],
    grantedKeywords: [],
    removedKeywords: [],
    enteredTurn: -1,
    controlSinceTurn: state.turn,
    counters: {},
    oncePerTurnUsed: [],
  };
  state.objects[instanceId] = o;
  emit(ctx, {
    type: 'TokenCreated',
    turn: state.turn,
    player: playerId,
    instanceId,
    name: def.name,
  });
  enterBattlefield(ctx, instanceId, {});
  return instanceId;
}

// ---------------------------------------------------------------------------
// Small state pokes
// ---------------------------------------------------------------------------

export function exhaustObject(ctx: Ctx, instanceId: string): void {
  const o = ctx.state.objects[instanceId];
  if (!o || o.zone !== 'battlefield' || o.exhausted) return;
  o.exhausted = true;
  emit(ctx, {
    type: 'Exhausted',
    turn: ctx.state.turn,
    player: o.controller,
    instanceId,
    name: nameOf(ctx.state, instanceId),
  });
}

export function readyObject(ctx: Ctx, instanceId: string): void {
  const o = ctx.state.objects[instanceId];
  if (!o || o.zone !== 'battlefield' || !o.exhausted) return;
  o.exhausted = false;
  emit(ctx, {
    type: 'Readied',
    turn: ctx.state.turn,
    player: o.controller,
    instanceId,
    name: nameOf(ctx.state, instanceId),
  });
}

export function freezeObject(ctx: Ctx, instanceId: string): void {
  const o = ctx.state.objects[instanceId];
  if (!o || o.zone !== 'battlefield') return;
  o.exhausted = true;
  if (o.frozen) return;
  o.frozen = true;
  emit(ctx, {
    type: 'Frozen',
    turn: ctx.state.turn,
    player: o.controller,
    instanceId,
    name: nameOf(ctx.state, instanceId),
  });
}

export function addBuff(
  ctx: Ctx,
  instanceId: string,
  attack: number,
  health: number,
  duration: 'turn' | 'permanent',
): void {
  const o = ctx.state.objects[instanceId];
  if (!o || o.zone !== 'battlefield') return;
  o.buffs.push({ attack, health, duration, appliedTurn: ctx.state.turn });
  emit(ctx, {
    type: 'Buffed',
    turn: ctx.state.turn,
    player: o.controller,
    instanceId,
    name: nameOf(ctx.state, instanceId),
    attack,
    health,
    duration,
  });
}

export function grantKeywordTo(
  ctx: Ctx,
  instanceId: string,
  keyword: Keyword,
  duration: 'turn' | 'permanent',
): void {
  const o = ctx.state.objects[instanceId];
  if (!o || o.zone !== 'battlefield') return;
  // Two keywords of the same name do not stack (RULES 7).
  if (o.grantedKeywords.some((g) => g.keyword === keyword && g.duration === duration)) return;
  o.grantedKeywords.push({ keyword, duration, appliedTurn: ctx.state.turn });
  emit(ctx, {
    type: 'KeywordGranted',
    turn: ctx.state.turn,
    player: o.controller,
    instanceId,
    name: nameOf(ctx.state, instanceId),
    keyword,
  });
}

export function removeKeywordFrom(
  ctx: Ctx,
  instanceId: string,
  keyword: Keyword,
  duration: 'turn' | 'permanent',
): void {
  const o = ctx.state.objects[instanceId];
  if (!o || o.zone !== 'battlefield') return;
  // A removal cancels an existing grant of the same keyword first.
  const granted = o.grantedKeywords.findIndex((g) => g.keyword === keyword);
  if (granted >= 0) o.grantedKeywords.splice(granted, 1);
  if (!o.removedKeywords.some((g) => g.keyword === keyword && g.duration === duration)) {
    o.removedKeywords.push({ keyword, duration, appliedTurn: ctx.state.turn });
  }
  emit(ctx, {
    type: 'KeywordRemoved',
    turn: ctx.state.turn,
    player: o.controller,
    instanceId,
    name: nameOf(ctx.state, instanceId),
    keyword,
  });
}

export function addGas(ctx: Ctx, playerId: string, chain: Chain | 'any', amount: number): void {
  if (amount <= 0) return;
  const key = chain === 'any' ? 'generic' : chain;
  ctx.state.players[playerId].gas[key] += amount;
  emit(ctx, {
    type: 'GasGenerated',
    turn: ctx.state.turn,
    player: playerId,
    chain: key,
    amount,
  });
}

/** Exhaust a Wallet for Gas. Returns the amount produced. */
export function tapWallet(ctx: Ctx, instanceId: string): number {
  const state = ctx.state;
  const o = state.objects[instanceId];
  if (!o || o.zone !== 'battlefield' || o.exhausted) return 0;
  const w = profileOf(o).wallet;
  if (!w) return 0;
  o.exhausted = true;
  const produced = (w.produces ?? 1) + walletExtraProduction(state, instanceId);
  addGas(ctx, o.controller, w.chain, produced);
  return produced;
}

/** Empty a player's gas pool (end of every phase and combat step, RULES 5). */
export function emptyPool(ctx: Ctx, playerId: string): void {
  const pool = ctx.state.players[playerId].gas;
  const lost = pool.generic + pool.btc + pool.eth + pool.sol + pool.bnb + pool.xrp;
  if (lost === 0) return;
  ctx.state.players[playerId].gas = {
    generic: 0,
    btc: 0,
    eth: 0,
    sol: 0,
    bnb: 0,
    xrp: 0,
  };
  emit(ctx, { type: 'GasPoolEmptied', turn: ctx.state.turn, player: playerId, lost });
}

export function setPriority(ctx: Ctx, playerId: string): void {
  if (ctx.state.priorityPlayer === playerId) return;
  ctx.state.priorityPlayer = playerId;
  ctx.state.passCount = 0;
  emit(ctx, { type: 'PriorityChanged', turn: ctx.state.turn, player: playerId });
}

// ---------------------------------------------------------------------------
// Triggers
// ---------------------------------------------------------------------------

export interface TriggerOptions {
  /** For `self:*` events: the object whose own abilities fire. */
  sourceInstanceId?: string;
  /** For `character:*` events: the object that caused the trigger. */
  triggeringInstanceId?: string;
  /** For controller-scoped events (turn:*, wallet:install, integrity:gain, token:create). */
  controller?: string;
  /** For `spell:play`: the stack item's controller and the card object. */
  spellInstanceId?: string;
  spellController?: string;
}

const SELF_EVENTS: TriggerEvent[] = [
  'self:deploy',
  'self:death',
  'self:attack',
  'self:block',
  'self:damagePlayer',
  'self:survivesDamage',
];

/**
 * Collect every triggered ability that matches `event` and queue it in
 * state.pendingTriggers. Queued triggers go on the stack the next time a
 * player would receive priority, in APNAP order.
 */
export function fireTriggers(ctx: Ctx, event: TriggerEvent, options: TriggerOptions): void {
  const state = ctx.state;
  if (SELF_EVENTS.includes(event)) {
    const id = options.sourceInstanceId;
    if (!id) return;
    queueFrom(ctx, id, event, options);
    return;
  }
  // Board-wide events: scan in APNAP order so the active player's triggers go
  // on the stack first.
  const order = apnapOrder(state);
  for (const pid of order) {
    const p = state.players[pid];
    const ids = [
      ...p.battlefield.wallets,
      ...p.battlefield.characters,
      ...p.battlefield.protocols,
      ...p.battlefield.upgrades,
    ];
    for (const id of ids) queueFrom(ctx, id, event, options);
  }
}

function apnapOrder(state: GameState): string[] {
  const active = state.activePlayer;
  const rest = state.playerOrder.filter((p) => p !== active);
  return [active, ...rest];
}

function queueFrom(ctx: Ctx, sourceId: string, event: TriggerEvent, options: TriggerOptions): void {
  const state = ctx.state;
  const o = state.objects[sourceId];
  if (!o) return;
  const prof = profileOf(o);
  prof.abilities.forEach((ability, abilityIndex) => {
    if (ability.kind !== 'triggered') return;
    if (ability.event !== event) return;

    // A dying object's own self:death still fires; everything else requires
    // the source to be on the battlefield.
    const selfDeath = event === 'self:death';
    if (!selfDeath && o.zone !== 'battlefield') return;

    // Controller-scoped events.
    if (
      (event === 'turn:start' ||
        event === 'turn:end' ||
        event === 'wallet:install' ||
        event === 'integrity:gain' ||
        event === 'token:create') &&
      options.controller !== undefined &&
      o.controller !== options.controller
    ) {
      return;
    }

    // Observer events with who/filter applied to the triggering object.
    if (event === 'character:deploy' || event === 'character:death') {
      const trigId = options.triggeringInstanceId;
      if (!trigId) return;
      const trig = state.objects[trigId];
      if (!trig) return;
      if (trigId === sourceId && ability.filter?.excludeSelf) return;
      if (!matchesFilter(state, trig, ability.filter, ability.who ?? 'friendly', o.controller, sourceId)) {
        return;
      }
    }

    if (event === 'spell:play') {
      const spellId = options.spellInstanceId;
      const spellController = options.spellController;
      if (!spellId || !spellController) return;
      const spell = state.objects[spellId];
      if (!spell) return;
      const who = ability.who ?? 'friendly';
      if (who === 'friendly' && spellController !== o.controller) return;
      if (who === 'enemy' && spellController === o.controller) return;
      if (!matchesFilter(state, spell, ability.filter, 'any', o.controller, sourceId)) return;
    }

    if (ability.oncePerTurn) {
      const key = `trigger:${abilityIndex}`;
      if (o.oncePerTurnUsed.includes(key)) return;
      o.oncePerTurnUsed.push(key);
    }

    // A trigger whose condition is false when it would fire does not fire.
    if (ability.condition && !evalCondition(state, ability.condition, o.controller, sourceId)) return;

    const item: StackItem = {
      id: nextId(ctx, 'st'),
      kind: 'trigger',
      controller: o.controller,
      sourceInstanceId: sourceId,
      abilityIndex,
      effects: ability.effects,
      targets: [],
      triggeringInstanceId: options.triggeringInstanceId,
      label: `${prof.name} (${event})`,
    };
    state.pendingTriggers.push(item);
    emit(ctx, {
      type: 'TriggerFired',
      turn: state.turn,
      player: o.controller,
      instanceId: sourceId,
      name: prof.name,
      event,
    });
  });
}

export { labelOf, opponentOf };
